/**
 * Avatar utility functions
 */

/**
 * Validates whether a string is a valid avatar URL (data URL or http/https URL)
 * Returns false for plain text strings that are not URLs
 */
export function isValidAvatarUrl(avatar: string | undefined | null): boolean {
  if (!avatar || typeof avatar !== 'string') {
    return false;
  }

  // Trim whitespace
  const trimmed = avatar.trim();

  // Check for data URLs (base64 encoded images)
  if (trimmed.startsWith('data:image/')) {
    return true;
  }

  // Check for http/https URLs
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    // Basic URL validation - must contain at least a domain-like structure
    try {
      new URL(trimmed);
      return true;
    } catch {
      return false;
    }
  }

  // Check for blob URLs (temporary browser URLs)
  if (trimmed.startsWith('blob:')) {
    return true;
  }

  // Any other string (like plain text names) is not a valid avatar URL
  return false;
}

/**
 * Gets initials from a name
 * @param name Full name string
 * @returns 1-2 character initials (uppercase)
 */
export function getInitials(name: string): string {
  if (!name || typeof name !== 'string') {
    return '';
  }

  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}
